import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Plus, Edit, Trash2, Eye, EyeOff, ArrowLeft, Save, X, 
  LogOut, FileText, Users, Mail, Briefcase 
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LoginPage } from "@/pages/LoginPage";
import { Navbar } from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const { isAuthenticated, isLoading, logout, getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState("blog");
  const [posts, setPosts] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  
  // Blog form
  const [blogForm, setBlogForm] = useState({
    title: "", slug: "", excerpt: "", content: "", category: "",
    author: "Tessa Authority", featured_image: "", published: false,
  });
  
  // Case Study form
  const [caseStudyForm, setCaseStudyForm] = useState({
    title: "", slug: "", client_name: "", industry: "", challenge: "",
    solution: "", results: [""], testimonial: "", testimonial_author: "",
    featured_image: "", published: false, order: 0,
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === "blog") {
        const response = await axios.get(`${API}/blog?published_only=false`);
        setPosts(response.data);
      } else if (activeTab === "case-studies") {
        const response = await axios.get(`${API}/case-studies?published_only=false`);
        setCaseStudies(response.data);
      } else if (activeTab === "newsletter") {
        const response = await axios.get(`${API}/newsletter/subscribers`, {
          headers: getAuthHeader()
        });
        setSubscribers(response.data);
      } else if (activeTab === "contacts") {
        const response = await axios.get(`${API}/contact`, {
          headers: getAuthHeader()
        });
        setContacts(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/[-\s]+/g, "-");
  };

  // Blog handlers
  const handleBlogChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBlogForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleBlogTitleChange = (e) => {
    const title = e.target.value;
    setBlogForm((prev) => ({
      ...prev,
      title,
      slug: prev.slug || generateSlug(title),
    }));
  };

  const handleBlogSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentItem) {
        await axios.put(`${API}/blog/${currentItem.id}`, blogForm, {
          headers: getAuthHeader()
        });
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/blog`, blogForm, {
          headers: getAuthHeader()
        });
        toast.success("Post created");
      }
      fetchData();
      resetForm();
    } catch (error) {
      toast.error("Failed to save post");
    }
  };

  const handleBlogEdit = (post) => {
    setCurrentItem(post);
    setBlogForm({
      title: post.title, slug: post.slug, excerpt: post.excerpt,
      content: post.content, category: post.category, author: post.author,
      featured_image: post.featured_image || "", published: post.published,
    });
    setIsEditing(true);
  };

  const handleBlogDelete = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await axios.delete(`${API}/blog/${postId}`, { headers: getAuthHeader() });
      toast.success("Post deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const toggleBlogPublish = async (post) => {
    try {
      await axios.put(`${API}/blog/${post.id}`, { published: !post.published }, {
        headers: getAuthHeader()
      });
      toast.success(post.published ? "Unpublished" : "Published");
      fetchData();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  // Case Study handlers
  const handleCaseStudyChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCaseStudyForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : name === "order" ? parseInt(value) || 0 : value,
    }));
  };

  const handleResultChange = (index, value) => {
    const newResults = [...caseStudyForm.results];
    newResults[index] = value;
    setCaseStudyForm((prev) => ({ ...prev, results: newResults }));
  };

  const addResult = () => {
    setCaseStudyForm((prev) => ({ ...prev, results: [...prev.results, ""] }));
  };

  const removeResult = (index) => {
    setCaseStudyForm((prev) => ({
      ...prev,
      results: prev.results.filter((_, i) => i !== index),
    }));
  };

  const handleCaseStudySubmit = async (e) => {
    e.preventDefault();
    const data = { ...caseStudyForm, results: caseStudyForm.results.filter(r => r.trim()) };
    try {
      if (currentItem) {
        await axios.put(`${API}/case-studies/${currentItem.id}`, data, {
          headers: getAuthHeader()
        });
        toast.success("Case study updated");
      } else {
        await axios.post(`${API}/case-studies`, data, {
          headers: getAuthHeader()
        });
        toast.success("Case study created");
      }
      fetchData();
      resetForm();
    } catch (error) {
      toast.error("Failed to save case study");
    }
  };

  const handleCaseStudyEdit = (study) => {
    setCurrentItem(study);
    setCaseStudyForm({
      title: study.title, slug: study.slug, client_name: study.client_name,
      industry: study.industry, challenge: study.challenge, solution: study.solution,
      results: study.results.length ? study.results : [""],
      testimonial: study.testimonial || "", testimonial_author: study.testimonial_author || "",
      featured_image: study.featured_image || "", published: study.published,
      order: study.order || 0,
    });
    setIsEditing(true);
  };

  const handleCaseStudyDelete = async (studyId) => {
    if (!window.confirm("Delete this case study?")) return;
    try {
      await axios.delete(`${API}/case-studies/${studyId}`, { headers: getAuthHeader() });
      toast.success("Case study deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const toggleCaseStudyPublish = async (study) => {
    try {
      await axios.put(`${API}/case-studies/${study.id}`, { published: !study.published }, {
        headers: getAuthHeader()
      });
      toast.success(study.published ? "Unpublished" : "Published");
      fetchData();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const resetForm = () => {
    setBlogForm({
      title: "", slug: "", excerpt: "", content: "", category: "",
      author: "Tessa Authority", featured_image: "", published: false,
    });
    setCaseStudyForm({
      title: "", slug: "", client_name: "", industry: "", challenge: "",
      solution: "", results: [""], testimonial: "", testimonial_author: "",
      featured_image: "", published: false, order: 0,
    });
    setCurrentItem(null);
    setIsEditing(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const categories = ["Strategy", "Infrastructure", "Authority", "Business Models", "Exit Planning", "Systems", "Leadership"];
  const industries = ["Consulting", "Technology", "Professional Services", "Healthcare", "Finance", "Education", "Other"];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <div className="min-h-screen bg-[#080808]" data-testid="admin-page">
      <Navbar />

      <div className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-serif text-4xl md:text-5xl font-light text-foreground">
                Admin Panel
              </h1>
            </div>
            <Button
              onClick={logout}
              variant="outline"
              className="border-white/10 text-muted-foreground hover:text-foreground"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-8 border-b border-white/5 pb-4">
            {[
              { id: "blog", label: "Blog Posts", icon: FileText },
              { id: "case-studies", label: "Case Studies", icon: Briefcase },
              { id: "newsletter", label: "Subscribers", icon: Mail },
              { id: "contacts", label: "Contacts", icon: Users },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); resetForm(); }}
                className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
                  activeTab === tab.id
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Blog Tab */}
          {activeTab === "blog" && (
            <>
              {!isEditing && (
                <div className="flex justify-end mb-6">
                  <Button
                    onClick={() => setIsEditing(true)}
                    className="bg-primary text-primary-foreground"
                    data-testid="new-post-button"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Post
                  </Button>
                </div>
              )}

              {isEditing ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-card/5 border border-white/5 p-8"
                >
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-serif text-2xl text-foreground">
                      {currentItem ? "Edit Post" : "New Post"}
                    </h2>
                    <Button variant="ghost" size="sm" onClick={resetForm}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  <form onSubmit={handleBlogSubmit} className="space-y-6" data-testid="blog-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">Title *</label>
                        <input type="text" name="title" value={blogForm.title} onChange={handleBlogTitleChange} required
                          className="w-full bg-transparent border border-white/10 focus:border-primary px-4 py-3 text-foreground" />
                      </div>
                      <div>
                        <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">Slug</label>
                        <input type="text" name="slug" value={blogForm.slug} onChange={handleBlogChange}
                          className="w-full bg-transparent border border-white/10 focus:border-primary px-4 py-3 text-foreground" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">Category *</label>
                        <select name="category" value={blogForm.category} onChange={handleBlogChange} required
                          className="w-full bg-[#080808] border border-white/10 focus:border-primary px-4 py-3 text-foreground">
                          <option value="">Select</option>
                          {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">Featured Image URL</label>
                        <input type="url" name="featured_image" value={blogForm.featured_image} onChange={handleBlogChange}
                          className="w-full bg-transparent border border-white/10 focus:border-primary px-4 py-3 text-foreground" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">Excerpt *</label>
                      <textarea name="excerpt" value={blogForm.excerpt} onChange={handleBlogChange} required rows={2}
                        className="w-full bg-transparent border border-white/10 focus:border-primary px-4 py-3 text-foreground resize-none" />
                    </div>
                    <div>
                      <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">Content *</label>
                      <textarea name="content" value={blogForm.content} onChange={handleBlogChange} required rows={12}
                        className="w-full bg-transparent border border-white/10 focus:border-primary px-4 py-3 text-foreground resize-y font-mono text-sm" />
                    </div>
                    <div className="flex items-center gap-3">
                      <input type="checkbox" name="published" id="published" checked={blogForm.published} onChange={handleBlogChange} className="accent-primary" />
                      <label htmlFor="published" className="text-sm text-muted-foreground">Publish immediately</label>
                    </div>
                    <div className="flex gap-4">
                      <Button type="submit" className="bg-primary text-primary-foreground">
                        <Save className="h-4 w-4 mr-2" />
                        {currentItem ? "Update" : "Create"}
                      </Button>
                      <Button type="button" variant="outline" onClick={resetForm} className="border-white/10">Cancel</Button>
                    </div>
                  </form>
                </motion.div>
              ) : (
                <div className="space-y-4">
                  {loading ? (
                    <p className="text-muted-foreground text-center py-12">Loading...</p>
                  ) : posts.length === 0 ? (
                    <div className="text-center py-12 bg-card/5 border border-white/5">
                      <p className="text-muted-foreground">No posts yet.</p>
                    </div>
                  ) : (
                    posts.map((post, index) => (
                      <div key={post.id} className="bg-card/5 border border-white/5 p-6 flex items-center justify-between gap-6" data-testid={`admin-post-${index}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-serif text-xl text-foreground truncate">{post.title}</h3>
                            <span className={`text-xs px-2 py-1 ${post.published ? "bg-green-500/10 text-green-400" : "bg-yellow-500/10 text-yellow-400"}`}>
                              {post.published ? "Published" : "Draft"}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">{post.category} • {formatDate(post.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => toggleBlogPublish(post)} className="text-muted-foreground hover:text-foreground">
                            {post.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleBlogEdit(post)} className="text-muted-foreground hover:text-foreground">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleBlogDelete(post.id)} className="text-muted-foreground hover:text-red-400">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}

          {/* Case Studies Tab */}
          {activeTab === "case-studies" && (
            <>
              {!isEditing && (
                <div className="flex justify-end mb-6">
                  <Button onClick={() => setIsEditing(true)} className="bg-primary text-primary-foreground" data-testid="new-case-study-button">
                    <Plus className="h-4 w-4 mr-2" />
                    New Case Study
                  </Button>
                </div>
              )}

              {isEditing ? (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-card/5 border border-white/5 p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-serif text-2xl text-foreground">{currentItem ? "Edit Case Study" : "New Case Study"}</h2>
                    <Button variant="ghost" size="sm" onClick={resetForm}><X className="h-4 w-4" /></Button>
                  </div>

                  <form onSubmit={handleCaseStudySubmit} className="space-y-6" data-testid="case-study-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">Title *</label>
                        <input type="text" name="title" value={caseStudyForm.title} onChange={(e) => setCaseStudyForm(prev => ({ ...prev, title: e.target.value, slug: prev.slug || generateSlug(e.target.value) }))} required
                          className="w-full bg-transparent border border-white/10 focus:border-primary px-4 py-3 text-foreground" />
                      </div>
                      <div>
                        <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">Client Name *</label>
                        <input type="text" name="client_name" value={caseStudyForm.client_name} onChange={handleCaseStudyChange} required
                          className="w-full bg-transparent border border-white/10 focus:border-primary px-4 py-3 text-foreground" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">Industry *</label>
                        <select name="industry" value={caseStudyForm.industry} onChange={handleCaseStudyChange} required
                          className="w-full bg-[#080808] border border-white/10 focus:border-primary px-4 py-3 text-foreground">
                          <option value="">Select</option>
                          {industries.map((ind) => <option key={ind} value={ind}>{ind}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">Featured Image URL</label>
                        <input type="url" name="featured_image" value={caseStudyForm.featured_image} onChange={handleCaseStudyChange}
                          className="w-full bg-transparent border border-white/10 focus:border-primary px-4 py-3 text-foreground" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">Challenge *</label>
                      <textarea name="challenge" value={caseStudyForm.challenge} onChange={handleCaseStudyChange} required rows={3}
                        className="w-full bg-transparent border border-white/10 focus:border-primary px-4 py-3 text-foreground resize-none" />
                    </div>
                    <div>
                      <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">Solution *</label>
                      <textarea name="solution" value={caseStudyForm.solution} onChange={handleCaseStudyChange} required rows={3}
                        className="w-full bg-transparent border border-white/10 focus:border-primary px-4 py-3 text-foreground resize-none" />
                    </div>
                    <div>
                      <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">Results *</label>
                      {caseStudyForm.results.map((result, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                          <input type="text" value={result} onChange={(e) => handleResultChange(index, e.target.value)}
                            placeholder={`Result ${index + 1}`}
                            className="flex-1 bg-transparent border border-white/10 focus:border-primary px-4 py-3 text-foreground" />
                          {caseStudyForm.results.length > 1 && (
                            <Button type="button" variant="ghost" onClick={() => removeResult(index)} className="text-red-400"><X className="h-4 w-4" /></Button>
                          )}
                        </div>
                      ))}
                      <Button type="button" variant="outline" onClick={addResult} className="border-white/10 mt-2">
                        <Plus className="h-4 w-4 mr-2" />Add Result
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">Testimonial</label>
                        <textarea name="testimonial" value={caseStudyForm.testimonial} onChange={handleCaseStudyChange} rows={3}
                          className="w-full bg-transparent border border-white/10 focus:border-primary px-4 py-3 text-foreground resize-none" />
                      </div>
                      <div>
                        <label className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">Testimonial Author</label>
                        <input type="text" name="testimonial_author" value={caseStudyForm.testimonial_author} onChange={handleCaseStudyChange}
                          className="w-full bg-transparent border border-white/10 focus:border-primary px-4 py-3 text-foreground" />
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <input type="checkbox" name="published" id="cs-published" checked={caseStudyForm.published} onChange={handleCaseStudyChange} className="accent-primary" />
                      <label htmlFor="cs-published" className="text-sm text-muted-foreground">Publish immediately</label>
                    </div>
                    <div className="flex gap-4">
                      <Button type="submit" className="bg-primary text-primary-foreground"><Save className="h-4 w-4 mr-2" />{currentItem ? "Update" : "Create"}</Button>
                      <Button type="button" variant="outline" onClick={resetForm} className="border-white/10">Cancel</Button>
                    </div>
                  </form>
                </motion.div>
              ) : (
                <div className="space-y-4">
                  {loading ? (
                    <p className="text-muted-foreground text-center py-12">Loading...</p>
                  ) : caseStudies.length === 0 ? (
                    <div className="text-center py-12 bg-card/5 border border-white/5">
                      <p className="text-muted-foreground">No case studies yet.</p>
                    </div>
                  ) : (
                    caseStudies.map((study, index) => (
                      <div key={study.id} className="bg-card/5 border border-white/5 p-6 flex items-center justify-between gap-6" data-testid={`admin-case-study-${index}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-serif text-xl text-foreground truncate">{study.title}</h3>
                            <span className={`text-xs px-2 py-1 ${study.published ? "bg-green-500/10 text-green-400" : "bg-yellow-500/10 text-yellow-400"}`}>
                              {study.published ? "Published" : "Draft"}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">{study.client_name} • {study.industry}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => toggleCaseStudyPublish(study)} className="text-muted-foreground hover:text-foreground">
                            {study.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleCaseStudyEdit(study)} className="text-muted-foreground hover:text-foreground">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleCaseStudyDelete(study.id)} className="text-muted-foreground hover:text-red-400">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}

          {/* Newsletter Tab */}
          {activeTab === "newsletter" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-6">
                <p className="text-muted-foreground">{subscribers.filter(s => s.status === 'active').length} active subscribers</p>
              </div>
              {loading ? (
                <p className="text-muted-foreground text-center py-12">Loading...</p>
              ) : subscribers.length === 0 ? (
                <div className="text-center py-12 bg-card/5 border border-white/5">
                  <p className="text-muted-foreground">No subscribers yet.</p>
                </div>
              ) : (
                subscribers.map((sub, index) => (
                  <div key={sub.id} className="bg-card/5 border border-white/5 p-4 flex items-center justify-between" data-testid={`subscriber-${index}`}>
                    <div>
                      <p className="text-foreground">{sub.email}</p>
                      <p className="text-xs text-muted-foreground">Subscribed: {formatDate(sub.subscribed_at)}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 ${sub.status === 'active' ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                      {sub.status}
                    </span>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Contacts Tab */}
          {activeTab === "contacts" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-6">
                <p className="text-muted-foreground">{contacts.length} contact submissions</p>
              </div>
              {loading ? (
                <p className="text-muted-foreground text-center py-12">Loading...</p>
              ) : contacts.length === 0 ? (
                <div className="text-center py-12 bg-card/5 border border-white/5">
                  <p className="text-muted-foreground">No contacts yet.</p>
                </div>
              ) : (
                contacts.map((contact, index) => (
                  <div key={contact.id} className="bg-card/5 border border-white/5 p-6" data-testid={`contact-${index}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-foreground font-medium">{contact.name}</h3>
                        <p className="text-sm text-primary">{contact.email}</p>
                      </div>
                      <span className="text-xs text-muted-foreground">{formatDate(contact.created_at)}</span>
                    </div>
                    {contact.company && <p className="text-sm text-muted-foreground mb-2">Company: {contact.company}</p>}
                    {contact.revenue && <p className="text-sm text-muted-foreground mb-2">Revenue: {contact.revenue}</p>}
                    <p className="text-sm text-muted-foreground border-t border-white/5 pt-4 mt-4">{contact.message}</p>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
